import { useQuery } from '@tanstack/react-query';
import { useAppBridge } from '@shopify/app-bridge-react';
import { makeApi } from '../api';
import type { InstallResponse } from '../types';

// See useDashboard.ts for the rationale on empty-string workerOrigin
// (same-origin via Cloudflare Workers route binding).
const WORKER_ORIGIN = '';

/**
 * Shopify always loads embedded apps with `?shop=<myshopify-domain>` in the
 * iframe URL — that's how App Bridge bootstraps. Reading the param here is
 * synchronous (vs. `app.idToken()` which is async + needs JWT decode) and
 * gives us a stable per-tenant key for query caching. Falls back to a
 * sentinel so the hook degrades gracefully in tests / non-embedded contexts.
 */
function shopFromUrl(): string {
  if (typeof window === 'undefined') return '__no_window__';
  return new URLSearchParams(window.location.search).get('shop') ?? '__no_shop_param__';
}

/**
 * Fires `POST /api/shopify/install` on SPA mount. The backend short-circuits
 * to `{status:'cached'}` when a valid session is already stored, so retries
 * (e.g. React Strict Mode double-render in development) are cheap.
 *
 * `retry: 0` — we surface install errors to the user immediately rather than
 * silently retrying. The "Try again" button in the failed-state UI calls
 * `refetch()` so the merchant controls re-attempts.
 *
 * The query key is scoped by the `shop` URL param. Cross-shop key collision
 * was theoretically possible with a global key in multi-shop dev scenarios
 * where one browser session hosts multiple embedded contexts; scoping
 * eliminates that. Removed `staleTime: Infinity` so a successful
 * Token Exchange in one mount doesn't pin a result that should re-verify
 * across a long-lived browser session.
 */
export function useInstall() {
  const app = useAppBridge();
  const shop = shopFromUrl();
  return useQuery<InstallResponse>({
    queryKey: ['shopify-install', shop],
    queryFn: () => makeApi(app, WORKER_ORIGIN).postInstall(),
    retry: 0,
    refetchOnWindowFocus: false,
  });
}
